import { slugify } from '@/lib/slug';
import { safeLocalStorage } from '@/lib/storage-utils';

export type BoardBackground = {
  type: 'gradient' | 'color' | 'image';
  value: string;
  overlay?: string;
};

export type HeaderConfigLike = {
  title: string;
  description: string;
  background: BoardBackground;
  hideBanner?: boolean;
  enableUpvotes?: boolean;
};

export type BoardListEntry = {
  id: string;
  title: string;
  description: string;
  background: BoardBackground;
  createdAt: string;
  slug: string;
};

const HEADER_LS_PREFIX = 'header-config-v1-';
const KANBAN_LS_PREFIX = 'kanban-state-v2-';
const BOARDS_LIST_KEY = 'boards-list';

export const generateUniqueSlug = (title: string): string => {
  const baseSlug = slugify(title);
  const existingSlugs = new Set<string>();

  for (let i = 0; i < safeLocalStorage.length; i++) {
    const key = safeLocalStorage.key(i);
    if (key?.startsWith(HEADER_LS_PREFIX)) {
      const slug = key.replace(HEADER_LS_PREFIX, '');
      existingSlugs.add(slug);
    }
  }

  if (!existingSlugs.has(baseSlug)) {
    return baseSlug;
  }

  let counter = 1;
  let candidateSlug = `${baseSlug}-${counter}`;
  while (existingSlugs.has(candidateSlug)) {
    counter++;
    candidateSlug = `${baseSlug}-${counter}`;
  }

  return candidateSlug;
};

export const updateBoardsList = (
  slug: string,
  next: { title: string; description: string; background: BoardBackground }
) => {
  const savedBoards = JSON.parse(safeLocalStorage.getItem(BOARDS_LIST_KEY) || '[]') as BoardListEntry[];
  const updatedBoards = savedBoards.map((board) =>
    board.slug === slug
      ? { ...board, title: next.title, description: next.description, background: next.background }
      : board
  );
  safeLocalStorage.setItem(BOARDS_LIST_KEY, JSON.stringify(updatedBoards));
};

export const migrateBoardSlug = (
  oldSlug: string,
  newSlug: string,
  newHeaderConfig: HeaderConfigLike
) => {
  const newHeaderKey = `${HEADER_LS_PREFIX}${newSlug}`;
  const oldHeaderKey = `${HEADER_LS_PREFIX}${oldSlug}`;
  const newKanbanKey = `${KANBAN_LS_PREFIX}${newSlug}`;
  const oldKanbanKey = `${KANBAN_LS_PREFIX}${oldSlug}`;

  const kanbanData = safeLocalStorage.getItem(oldKanbanKey);
  if (kanbanData) {
    safeLocalStorage.setItem(newKanbanKey, kanbanData);
    safeLocalStorage.removeItem(oldKanbanKey);
  }

  safeLocalStorage.setItem(newHeaderKey, JSON.stringify(newHeaderConfig));
  safeLocalStorage.removeItem(oldHeaderKey);

  const savedBoards = JSON.parse(safeLocalStorage.getItem(BOARDS_LIST_KEY) || '[]') as BoardListEntry[];
  const updatedBoards = savedBoards.map((board) =>
    board.slug === oldSlug
      ? {
          ...board,
          slug: newSlug,
          title: newHeaderConfig.title,
          description: newHeaderConfig.description,
          background: newHeaderConfig.background,
        }
      : board
  );
  safeLocalStorage.setItem(BOARDS_LIST_KEY, JSON.stringify(updatedBoards));
};


